import { IPacketDto } from '@r1-frontend/api-domru/api-epg/v1/dto/IPacketDto';
import { ECategory } from '@r1-frontend/api-domru/api-profile/v1/spec-offer/update/dto/ResponseDto';

import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import type { IPrices } from '~/src/components/Promo/popups/PartnerPopup/types';
import type { IPromoItem } from '~/src/components/Promo/popups/PartnerPopup/types';
import type { IGetTextButtonPartner } from '~/src/features/promo/types';
import type { IManiaCardData } from '~/src/features/promo/types';
import { ddmmyyyy } from '~/src/helpers/date';
import type { IPromoStore } from '~/src/store/reducers/promo';

/**
 * Получить изображение карточки
 *
 * @function
 * @name getImage
 * @param {IPromoItem.picture} picture - один из параметров отвечающих изображения
 * @param {IPromoItem.banner} banner - один из параметров отвечающих изображения
 * @param {IShopItem.image} image - один из параметров отвечающих изображения
 * @param {IShopItem.image_webp} image_webp - один из параметров отвечающих изображения
 * @returns {string}
 */
export const getImage = ({ picture, banner, image }: IPromoItem): string => {
    const defaultUrl = '/static/images/gift.png';
    return picture || banner || image || defaultUrl;
};

/**
 * Получить текст кнопки карточки
 *
 * @function
 * @name getButtonText
 * @param {IShopItem.button_text} button_text - текст кнопки
 * @param {IPromoItem.category} category - объект с данными, включающий параметр с текстом кнопки
 * @returns {string}
 */
export const getButtonText = ({ button_text, category }: IPromoItem): string => {
    const getCategoryText = () => {
        if (category && typeof category === 'object') {
            return category.button_text;
        }
        return 'Подробнее';
    };
    return button_text || getCategoryText();
};

/**
 * Получить описание карточки
 *
 * @function
 * @name getDescription
 * @param {IPromoItem.shortDescription} shortDescription - один из параметров отвечающих за описание карточки
 * @param {IShopItem.body} body - один из параметров отвечающих описание за карточки
 * @param {IPromoItem.description} description - один из параметров отвечающих за описание карточки
 * @param {IShopItem.body_full} body_full - один из параметров отвечающих за описание карточки
 * @returns {string}
 */
export const getDescription = ({ shortDescription, body, description, body_full }: IPromoItem): string => {
    return shortDescription || body || description || body_full;
};

/**
 * Получить дату окончания акции карточки
 *
 * @function
 * @name getStopDate
 * @param {IPromoItem.requestStopDate} requestStopDate - дата окончания акции
 * @param {IPromoItem.closeDate} closeDate - дата окончания акции
 * @param {IPromoStore.category} category - категории карточки
 * @returns {string}
 */
export const getStopDate = (
    requestStopDate: IPromoItem['requestStopDate'],
    closeDate: IPromoItem['closeDate'],
    category:IPromoStore['category'],
): string => {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        timeZone: 'UTC',
    };
    const oneDayCollision = 1;
    const dateFinal = requestStopDate || closeDate;

    if (category === ECategory.connected && dateFinal) {
        const date = new Date(dateFinal);
        if (requestStopDate) {
            return `<p>Действует до ${ddmmyyyy(date)}</p>`;
        }
        if (closeDate) {
            date.setDate(date.getDate() + oneDayCollision);
            return `<p>Действует до ${date.toLocaleString('ru', options)}</p>`;
        }
    }
    return '';
};

/**
 * Получить текст кнопки карточки партнерских сервисов
 *
 * @function
 * @name getButtonTextForPartner
 * @param {boolean} exists - один из параметров активности карточки
 * @param {IShopCategory} category - данные объекта категории
 * @param {string} buttonText - текст кнопки
 * @param {boolean} isConnected - статус карточки
 * @returns {string}
 */
export const getButtonTextForPartner = ({
    exists,
    category,
    button_text: buttonText,
}: IGetTextButtonPartner, isConnected: boolean): string => {
    if (typeof category !== 'object') {
        return 'Подробнее';
    }
    const { button_text_exists: buttonTextExists, button_text: buttonTextCategory } = category || {};
    if (!exists) {
        return (isConnected ? buttonTextExists : buttonText) || buttonTextCategory;
    }

    return buttonTextExists;
};

/**
 * @name monthText
 * @type {(number: number) => string}
 */
const monthText = getPluralize(monthPluralize);

/**
 * Получить промо текст для карточки
 *
 * @function
 * @name getPromoPartnerText
 * @param {IPrices} pricesPromo - объект с данными по промо цене
 * @returns {string}
 */
export const getPromoPartnerText = ({ pricesPromo }: { pricesPromo: IPrices }): string => {
    const ONE_MONTH = 1;
    if (pricesPromo?.rent?.month) {
        const { rent : { month } } = pricesPromo;
        return `
            ${month.lifetime_price > ONE_MONTH ? 'Первые' : 'Первый'}
            ${month.lifetime_price > ONE_MONTH ? month.lifetime_price : ''}
            ${monthText(month.lifetime_price)}
            ${month.value} ₽/мес.
        `;
    }
    return '';
};

/**
 * Получить данные по карточке "Рекомендуйте Дом.ру"
 *
 * @function
 * @name getManiaCardData
 * @param {number} reward - сумма вознагрождения
 * @returns {IManiaCardData}
 */
export const getManiaCardData = (reward: number): IManiaCardData => {
    return {
        textNotConnected: 'Рекомендовать',
        imgUrl: '/static/images/promo/mania.png',
        imgSize: 'contain',
        title: 'Рекомендуйте Дом.ру и получайте деньги на карту',
        description: `Платим до ${reward} ₽, если по вашей рекомендации подключили услуги Дом.ру.`,
    };
};

/*
* Получает преобразованный массив каналов
*/
export const getExpandedChannels = (data: IPacketDto[], tvPackageId: number) =>{
    return (data[0]?.channels || []).map(channel => {
        return ({
            ...channel,
            bids: tvPackageId,
            biglogo: channel.channelInfo ?  channel.channelInfo.bigLogo : '',
            button: channel.lcn,
            chid: channel.channelId,
            description: channel.channelInfo ?  channel.channelInfo.description : '',
            display: channel.channelInfo ?  channel.channelInfo.display : '',
            hd: channel.quality === 'HD' ? '1' : '0',
            logo: channel?.channelInfo?.logo ? channel.channelInfo.logo : '',
            title: channel.title,
            url: channel?.channelInfo?.logo ? channel.channelInfo.url : '',
            xvid: channel.epgId,
            thid: '',
        });
    });
};
